import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import DialPad from './DialPad';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';

function Register({ orgId, onBack, onSuccess }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [existingChildren, setExistingChildren] = useState([]);
  const [parentName, setParentName] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [children, setChildren] = useState([{ name: '', notes: '' }]);
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkedInNames, setCheckedInNames] = useState([]);

  const checkDailyLimit = async () => {
    const orgData = await supabase
      .from('organizations')
      .select('plan')
      .eq('id', orgId)
      .single();

    if (orgData.data?.plan === 'free') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { count } = await supabase
        .from('checkins')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', orgId)
        .gte('checked_in_at', today.toISOString());

      if (count >= 15) return false;
    }
    return true;
  };

  const handlePhoneSearch = async () => {
    setError('');
    if (!phone) {
      setError(t.pleaseEnterPhone);
      return;
    }

    setLoading(true);

    const { data, error } = await supabase
      .from('children')
      .select('*')
      .ilike('parent_phone', `%${phone}%`);

    if (!error && data.length > 0) {
      const childIds = data.map((c) => c.id);
      const { data: activeCheckins } = await supabase
        .from('checkins')
        .select('child_id')
        .in('child_id', childIds)
        .eq('status', 'checked_in');

      const activeIds = new Set((activeCheckins || []).map((c) => c.child_id));
      const childrenWithStatus = data.map((c) => ({
        ...c,
        alreadyCheckedIn: activeIds.has(c.id),
      }));

      setLoading(false);
      setExistingChildren(childrenWithStatus);
      setParentName(data[0].parent_name);
      setPin(data[0].pickup_pin || '');
      setStep(2);
    } else {
      setLoading(false);
      setStep(3);
    }
  };

  const toggleChildSelection = (child) => {
    setSelectedChildren((prev) =>
      prev.find((c) => c.id === child.id)
        ? prev.filter((c) => c.id !== child.id)
        : [...prev, child]
    );
  };

  const handleCheckInSelected = async () => {
    if (selectedChildren.length === 0) {
      setError('Please select at least one child to check in.');
      return;
    }

    const allowed = await checkDailyLimit();
    if (!allowed) {
      setError(t.dailyLimitReached);
      return;
    }

    setLoading(true);
    setError('');

    for (const child of selectedChildren) {
      const { error } = await supabase
        .from('checkins')
        .insert([{
          child_id: child.id,
          organization_id: orgId,
          status: 'checked_in',
          checked_in_at: new Date()
        }]);

      if (error) {
        setError('Error checking in ' + child.full_name + ': ' + error.message);
        setLoading(false);
        return;
      }
    }

    setLoading(false);
    const names = selectedChildren.map((c) => c.full_name).join(', ');
    onSuccess(`✅ ${names} ${selectedChildren.length > 1 ? 'have' : 'has'} been checked in!`);
  };

  const handleAddChild = () => {
    setChildren([...children, { name: '', notes: '' }]);
  };

  const handleRemoveChild = (index) => {
    if (children.length === 1) return;
    setChildren(children.filter((_, i) => i !== index));
  };

  const handleChildChange = (index, field, value) => {
    const updated = children.map((child, i) =>
      i === index ? { ...child, [field]: value } : child
    );
    setChildren(updated);
  };

  const handleRegister = async () => {
    setError('');

    if (!parentName || !phone) {
      setError(t.pleaseFillFields);
      return;
    }

    if (children.some((c) => !c.name)) {
      setError(t.pleaseEnterChildName);
      return;
    }

    if (!pin || pin.length !== 4) {
      setError(t.pleaseEnter4DigitPin);
      return;
    }

    if (pin !== confirmPin) {
      setError(t.pinsDoNotMatch);
      return;
    }

    const allowed = await checkDailyLimit();
    if (!allowed) {
      setError('⚠️ You have reached your daily limit of 15 check-ins. Upgrade to Pro for unlimited check-ins!');
      return;
    }

    setLoading(true);

    const checkedIn = [];

    for (const child of children) {
      const { data, error } = await supabase
        .from('children')
        .insert([{
          full_name: child.name,
          parent_name: parentName,
          parent_phone: phone,
          emergency_contact_name: emergencyName,
          emergency_contact_phone: emergencyPhone,
          notes: child.notes,
          pickup_pin: pin,
          organization_id: orgId
        }])
        .select();

      if (error) {
        setError('Error registering ' + child.name + ': ' + error.message);
        setLoading(false);
        return;
      }

      await supabase
        .from('checkins')
        .insert([{
          child_id: data[0].id,
          organization_id: orgId,
          status: 'checked_in',
          checked_in_at: new Date()
        }]);

      checkedIn.push(child.name);
    }

    setLoading(false);
    onSuccess(`✅ ${checkedIn.join(', ')} ${checkedIn.length > 1 ? 'have' : 'has'} been registered and checked in!`);
  };

  return (
    <div>
      <div className="top-bar">
        <button className="btn btn-gray" onClick={onBack}>{t.back}</button>
      </div>

      {/* Step 1 - Phone Search */}
      {step === 1 && (
        <div>
          <div className="header">
            <h2>{t.checkInTitle}</h2>
            <p>{t.enterPhonePrompt}</p>
          </div>

          <DialPad value={phone} onChange={setPhone} maxLength={10} />

          <button className="btn btn-primary" onClick={handlePhoneSearch} disabled={loading}>
            {loading ? t.searching : t.continue}
          </button>

          {error && <div className="error-message">{error}</div>}
        </div>
      )}

      {/* Step 2 - Returning Parent */}
      {step === 2 && (
        <div>
          <div className="header">
            <h2>{t.welcomeBack}</h2>
            <p>{t.welcomeBackSub(parentName)}</p>
          </div>

          {existingChildren.map((child) => {
            const selected = !!selectedChildren.find((c) => c.id === child.id);
            const alreadyIn = child.alreadyCheckedIn;
            return (
              <div
                key={child.id}
                className="kid-card"
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: alreadyIn ? 'not-allowed' : 'pointer',
                  border: alreadyIn ? '2px solid #ccc' : selected ? '2px solid #1a56db' : undefined,
                  background: alreadyIn ? '#f5f5f5' : selected ? '#f0f4ff' : undefined,
                  opacity: alreadyIn ? 0.7 : 1,
                }}
                onClick={() => !alreadyIn && toggleChildSelection(child)}
              >
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  border: `2px solid ${alreadyIn ? '#ccc' : '#1a56db'}`,
                  background: alreadyIn ? '#e0e0e0' : selected ? '#1a56db' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, color: alreadyIn ? '#999' : '#fff', fontSize: '14px'
                }}>
                  {alreadyIn ? '✓' : selected ? '✓' : ''}
                </div>
                <div className="kid-info">
                  <h3>👶 {child.full_name}</h3>
                  {alreadyIn
                    ? <p style={{ color: '#43a047', fontWeight: '600' }}>{t.alreadyCheckedIn}</p>
                    : <p>{t.emergency}: {child.emergency_contact_name || t.notSet}</p>
                  }
                  {child.notes && !alreadyIn && <p>{t.notes}: {child.notes}</p>}
                </div>
              </div>
            );
          })}

          <button
            className="btn btn-primary"
            style={{ marginTop: '8px' }}
            onClick={handleCheckInSelected}
            disabled={loading || selectedChildren.length === 0}
          >
            {loading ? t.checkingIn : t.checkInSelected(selectedChildren.length)}
          </button>

          <hr className="divider" />
          <button className="btn btn-primary" onClick={() => setStep(3)}>
            {t.registerAnotherChild}
          </button>

          {error && <div className="error-message">{error}</div>}
        </div>
      )}

      {/* Step 3 - New Registration Form */}
      {step === 3 && (
        <div>
          <div className="header">
            <h2>{t.registerChildren}</h2>
            <p>{t.fillInfoBelow}</p>
          </div>

          <p className="section-title">{t.parentInfo}</p>
          <input
            className="input"
            placeholder={t.parentFullName}
            value={parentName}
            onChange={(e) => setParentName(e.target.value)}
          />
          <p style={{ textAlign: 'center', fontWeight: '600', marginBottom: '4px', color: '#444' }}>{t.parentPhone}</p>
          <DialPad value={phone} onChange={setPhone} maxLength={10} />

          <p className="section-title">{t.emergencyContact}</p>
          <input
            className="input"
            placeholder={t.emergencyContactName}
            value={emergencyName}
            onChange={(e) => setEmergencyName(e.target.value)}
          />
          <p style={{ textAlign: 'center', fontWeight: '600', marginBottom: '4px', color: '#444' }}>{t.emergencyContactPhone}</p>
          <DialPad value={emergencyPhone} onChange={setEmergencyPhone} maxLength={10} />

          <p className="section-title">{t.createPickupPin}</p>
          <p style={{ color: '#888', fontSize: '14px', marginBottom: '12px' }}>{t.pinRequired}</p>
          <p style={{ textAlign: 'center', fontWeight: '600', marginBottom: '4px', color: '#444' }}>{t.createPin}</p>
          <DialPad value={pin} onChange={setPin} maxLength={4} />
          <p style={{ textAlign: 'center', fontWeight: '600', marginBottom: '4px', marginTop: '8px', color: '#444' }}>{t.confirmPin}</p>
          <DialPad value={confirmPin} onChange={setConfirmPin} maxLength={4} />

          <p className="section-title">{t.children}</p>
          <p style={{ color: '#888', fontSize: '14px', marginBottom: '12px' }}>{t.addAllChildren}</p>

          {children.map((child, index) => (
            <div key={index} style={childCardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <p style={{ fontWeight: '700', color: '#1a1a2e', margin: 0 }}>
                  {t.childNumber(index + 1)}
                </p>
                {children.length > 1 && (
                  <button className="btn btn-red btn-small" onClick={() => handleRemoveChild(index)}>
                    {t.remove}
                  </button>
                )}
              </div>
              <input
                className="input"
                placeholder={t.childFullName}
                value={child.name}
                onChange={(e) => handleChildChange(index, 'name', e.target.value)}
              />
              <input
                className="input"
                placeholder={t.notesOptional}
                value={child.notes}
                onChange={(e) => handleChildChange(index, 'notes', e.target.value)}
              />
            </div>
          ))}

          <button className="btn btn-blue" style={{ width: '100%', marginBottom: '16px' }} onClick={handleAddChild}>
            {t.addAnotherChild}
          </button>

          <button className="btn btn-primary" onClick={handleRegister} disabled={loading}>
            {loading ? t.registering : t.registerAndCheckIn(children.length)}
          </button>

          {existingChildren.length > 0 && (
            <button className="btn btn-gray" style={{ width: '100%', marginTop: '10px' }} onClick={() => setStep(2)}>
              {t.backToMyChildren}
            </button>
          )}

          {error && <div className="error-message">{error}</div>}
        </div>
      )}
    </div>
  );
}

const childCardStyle = {
  background: '#f8faff',
  border: '2px solid #e8f0fe',
  borderRadius: '16px',
  padding: '20px',
  marginBottom: '16px',
};

export default Register;